import React, { useState, useEffect, useRef, useReducer } from 'react';
// navigation
import {
  createNavigator
  , SwitchRouter
  , SceneView
} from "@react-navigation/core";
import { createBrowserApp, Link } from "@react-navigation/web";
import {scenes} from './scenes/Scenes'
import { currentLang, currentLangParam } from './components/components/utils';

// Internationalization and Localization
import {IntlProvider, FormattedMessage}  from 'react-intl';
import messages_en from "./translations/en.json";
import messages_fr from "./translations/fr.json";

// Template Components
import AppContext from './components/AppContext';
import Header from './templates/Header';
import GlobalSpinner from './templates/GlobalSpinner';
import GlobalAlerts from './templates/GlobalAlerts';
import DateModified from './templates/DateModified';
import Footer from './templates/Footer';
import { Auth, API } from 'aws-amplify';

// Popup
import MagnifiquePopup from './components/MagnifiquePopup.js'
import FormidableSessionTimeout from './components/FormidableSessionTimeout.jsx'


const TemplateView = (props) => {
	const [versionNumber] = useState(props.version);
	const [dateModified] = useState( !!props.dateModified ? props.dateModified : Date.now() );
	const [messages] = useState({ en: messages_en, fr: messages_fr, });

	let flashSuccessAnimationTimerID = useRef(undefined);
	let flashNoticeAnimationTimerID = useRef(undefined);
	let flashWarningAnimationTimerID = useRef(undefined);
	let flashErrorAnimationTimerID = useRef(undefined);
	let flashAlerts = useRef({
		flashSuccess: null,
		flashWarning: null,
		flashNotice: null,
		flashError: null
	});
	const startTime = useRef({flashSuccess: null, flashNotice: null, flashWarning: null, flashError: null});
	const [currentPageIndex,setCurrentPageIndex] = useState(-1);

	// Context vars
	const [existingUser, setExistingUser] = useState(undefined);
	const [accountData, setAccountData] = useState({});
	const [contactData, setContactData] = useState({});
	const [codeTables, setCodeTables] = useState(undefined);
	const [codeTableLookup, setCodeTableLookup] = useState(undefined);
	const [dynamicPageTitle, setDynamicPageTitle] = useState(undefined);
	const [websocket, setWebsocket] = useState(null);
	const [globalSpinnerCount, setGlobalSpinnerCount] = useState({count:0});
	const [, globalForceUpdate] = useReducer(x => x + 1, 0);

	// Global alerts
	const [globalAlertDuration] = useState(3000);

	// Session timeout
	const [pauseTimeout, setPauseTimeout] = useState(false);

	const { descriptors, navigation } = props;
	const activeKey = navigation?.state.routes[navigation?.state.index].key;
	const urlParams = useState(document.location.search);

	const descriptor = !!descriptors && !!activeKey ?
		descriptors[activeKey] :
		{navigation: {}, options: {}, getComponent: () => { return "Home"; }};

	const appContextValues = {
		existingUser, setExistingUser,
		accountData, setAccountData,
		contactData, setContactData,
		codeTables, setCodeTables,
		codeTableLookup, setCodeTableLookup,
		globalSpinnerCount, setGlobalSpinnerCount,
		pauseTimeout, setPauseTimeout,
		globalForceUpdate,
		dynamicPageTitle, setDynamicPageTitle,
		websocket, setWebsocket,
		updateAlerts: () => {}, //defined in GlobalAlerts to prevent triggering updates to other components

		//TODO: Pretty much all the alert code should be moved out of App.js and probably belongs in AppContext or GlobalAlerts
		setFlashSuccess: (bannerMessages) => { setFlashBannerMessages(bannerMessages, flashSuccessAnimationTimerID, "flashSuccess", "successAlertTimerBar"); },
		setFlashNotice: (bannerMessages) => { setFlashBannerMessages(bannerMessages, flashNoticeAnimationTimerID, "flashNotice", "noticeAlertTimerBar"); },
		setFlashWarning: (bannerMessages) => { setFlashBannerMessages(bannerMessages, flashWarningAnimationTimerID, "flashWarning", "warningAlertTimerBar"); },
		setFlashError: (bannerMessages) => { setFlashBannerMessages(bannerMessages, flashErrorAnimationTimerID, "flashError", "errorAlertTimerBar"); }
	}

	useEffect(() => {
		localStorage.setItem("languageSwitchActiveKey", activeKey);
		if (activeKey !== "Callback" && activeKey !== "InitiateAuth" && activeKey !== "Login" && activeKey !== "Menu" && activeKey !== "Account_Edit") {
			//saving location of where to redirect to after login
			if (activeKey !== "SuccessPage") {
				localStorage.setItem("activeKey", activeKey);
			}
			//saving the urlParameters used when setting this redirect (preserves selected language and proceedingId, if applicable)
			localStorage.setItem("urlParameters", new URLSearchParams(document.location.search).toString());

			//if active key is the "SecureEFilingSplash", then after login, just redirect them to the "SecureEFiling" form
			if (activeKey === "SecureEFilingSplash") {
				localStorage.setItem("activeKey", "SecureEFiling");
			}
		}
	}, [activeKey, urlParams])

	//checking if the page has changed and clearing out any global alerts from the previous page render
	useEffect(() => {
		if (currentPageIndex !== navigation?.state.index ?? currentPageIndex) {

			if ((flashAlerts?.current?.flashNotice?.duration ?? 0) < 0) {flashAlerts.current.flashNotice.duration += 1}
			if ((flashAlerts?.current?.flashSuccess?.duration ?? 0) < 0) {flashAlerts.current.flashSuccess.duration += 1}
			if ((flashAlerts?.current?.flashWarning?.duration ?? 0) < 0) {flashAlerts.current.flashWarning.duration += 1}
			if ((flashAlerts?.current?.flashError?.duration ?? 0) < 0) {flashAlerts.current.flashError.duration += 1}

			flashAlerts.current = {
				flashNotice: !!flashAlerts.current.flashNotice && flashAlerts.current.flashNotice.duration !== 0 ? flashAlerts.current.flashNotice : (window.env["environment"] !== "prod" ?
					{duration:-1, messages:[<FormattedMessage id="test.warning"/>]}
					: null
				),
				flashSuccess: !!flashAlerts.current.flashSuccess && flashAlerts.current.flashSuccess.duration !== 0 ? flashAlerts.current.flashSuccess : null,
				flashWarning: !!flashAlerts.current.flashWarning && flashAlerts.current.flashWarning.duration !== 0 ? flashAlerts.current.flashWarning : null,
				flashError: !!flashAlerts.current.flashError && flashAlerts.current.flashError.duration !== 0 ? flashAlerts.current.flashError : null
			};
			setCurrentPageIndex(navigation?.state.index);
		}
	}, [currentPageIndex, navigation.state.index]);

	// change tab title (web page / document title) dynamically according to current language
	useEffect(() => {
		const language = currentLangParam()?.lang?.split('_')[0];
		if (language === 'fr') {
			document.title = 'Application Web DEDAC - ASFC - Canada.ca';
		} else {
			document.title = 'ACE Web Application - CBSA - Canada.ca';
		}
	}, [currentLangParam()])


	function setFlashBannerMessages(messages, animationTimerID, stateKey, timerBarElementID) {
		if (animationTimerID) {
			clearInterval(animationTimerID.current)
		}
		let msgObj = getFlashMsgObj(messages);
		flashAlerts.current[stateKey] = msgObj;
		startTime.current[stateKey] = Date.now();
		animationTimerID.current = setInterval(
			() => updateCSS(flashAlerts[stateKey]
					, stateKey
					, animationTimerID
					, timerBarElementID
					, msgObj)
			, 1
		);

		appContextValues.updateAlerts();
	}

	function getFlashMsgObj(messages) {
		if (messages === null || messages === undefined) {
			return messages;
		}
		//inserting singular message into an array if it's not already in an array
		if (messages instanceof Array) {
			return {messages: messages};
		}
		if (!!messages.messages) {
			return messages;
		}
		return {messages: [messages]};
	}

	function updateCSS(thisStateFlash, stateKey, thisFlashAnimationTimerID, timerBarElementID, msgObj) {
		let duration = !!msgObj?.duration ? msgObj.duration : globalAlertDuration * (msgObj?.messages?.length ?? 1);

		if (duration > 0) {
			incrementTimerBar(stateKey, thisFlashAnimationTimerID, timerBarElementID, duration);
		} else {
			if (document.getElementById(timerBarElementID)) {
				document.getElementById(timerBarElementID).style.width = '0%';
			}
			clearInterval(thisFlashAnimationTimerID.current);
		}
	}

	function incrementTimerBar(stateKey, thisFlashAnimationTimerID, timerBarElementID, duration) {
		let nextPercent = (Date.now() - startTime.current[stateKey]) / duration * 100;

		if (document.getElementById(timerBarElementID)) {
			document.getElementById(timerBarElementID).style.width = nextPercent >= 100 ? 100 : nextPercent +'%';
		}

		if (nextPercent >= 100) {
			clearInterval(thisFlashAnimationTimerID.current);
			setTimeout(() => autoUnmountFlashMessage(stateKey), 100);
		}
	}

	function autoUnmountFlashMessage(stateKey) {
		flashAlerts.current[stateKey] = null;
		startTime.current[stateKey] = null;
		appContextValues.updateAlerts()
	}

	async function handleSignOut() {
		const synchronizeSignOut = async () => {
			try {
				//disconnect websocket
				if (!!websocket) {
					websocket.close();
					setWebsocket(null);
				}

				//initiate global logout
				let apiURL = "logout";
				await API.get("backend-api", apiURL).then(function(response){
				}).catch((err) => {
					if (window.env.environment !== "test" && window.env.environment !== "prod") {
						console.log("error while attempting to sign out");
					}
				});

				//sign out of cognito locally
				await Auth.signOut();
			} catch (error) {
				console.log('error signing out: ', error);
			}
		}
		synchronizeSignOut();
	}

	function handleSignIn() {
		navigation.navigate("Login", currentLangParam());
	}
	function handleNavigateToEFiling() {
		navigation.navigate("SecureEFilingSplash", currentLangParam());
	}
	function handleNavigateToAccount() {
		navigation.navigate("Account", currentLangParam());
	}

	const [appearPopupLogOut, setAppearPopupLogOut] = useState(false);
	const [appearPopupLogIn, setAppearPopupLogIn] = useState(false);
	const [appearPopupEFiling, setAppearPopupEFiling] = useState(false);
	const [appearPopupAccount, setAppearPopupAccount] = useState(false);

	return (
		<IntlProvider locale={currentLang()} messages={messages[currentLang()]}>
		  <Header
		    pageTitle={descriptor.options.pageTitle}
		    breadcrumb={fillInMissingBreadcrumbParams(descriptor.options.breadcrumb, descriptor.state.params)}
		    breadcrumbTitle={descriptor.options.breadcrumbTitle}
		    currentMenuItem={activeKey}
		  />


		  <main className="container" typeof="WebPageElement" property="mainContentOfPage">
		    <AppContext.Provider value={appContextValues}>
		    <MagnifiquePopup
		        id="signoutConfirmation"
		        trigger={appearPopupLogOut}
		        setTrigger={setAppearPopupLogOut}
		        continueCallback={() => {handleSignOut(); appContextValues.setGlobalSpinnerCount(appContextValues.globalSpinnerCount + 1);}}
		        header={<FormattedMessage id="message.signout.confirmation" />}
		        body={<FormattedMessage id="message.signout.explanation" />}
		      />
		      <MagnifiquePopup
		        id="signinConfirmation"
		        trigger={appearPopupLogIn}
		        setTrigger={setAppearPopupLogIn}
		        continueCallback={() => {handleSignIn();}}
		        header={<FormattedMessage id="message.signin.confirmation" />}
		        body={<FormattedMessage id="message.signin.explanation" />}
		      />
		      <MagnifiquePopup
		        id="eFilingConfirmation"
		        trigger={appearPopupEFiling}
		        setTrigger={setAppearPopupEFiling}
		        continueCallback={() => {handleNavigateToEFiling();}}
		        header={<FormattedMessage id="message.navigatingToEFiling.confirmation" />}
		        body={<FormattedMessage id="message.navigatingToEFiling.explanation" />}
		      />
		      <MagnifiquePopup
		        id="accountConfirmation"
		        trigger={appearPopupAccount}
		        setTrigger={setAppearPopupAccount}
		        continueCallback={() => {handleNavigateToAccount();}}
		        header={<FormattedMessage id="message.navigatingToAccount.confirmation" />}
		        body={<FormattedMessage id="message.navigatingToAccount.explanation" />}
		      />
		      <GlobalSpinner />
		      <GlobalAlerts flashAlerts={flashAlerts.current}/>
		      {descriptor.options.pageTitle !== "" && descriptor.options.pageTitle !== undefined ? // "paddingRight" is hard coded to 4.2rem * the number of <span className="iconLink"> which to this date is 3.
		      		<h1 id="wb-cont" property="name" style={{ position : "relative", textOverflow : "ellipsis", paddingRight: "12.6rem", marginTop: "0.3rem" }}>
		      		  {descriptor.options.pageTitle}
		      		</h1>
		      	: !!dynamicPageTitle ?
		      			<h1 id="wb-cont" property="name" style={{ position : "relative", textOverflow : "ellipsis", paddingRight: "12.6rem", marginTop: "0.3rem" }}>
		      			  {dynamicPageTitle}
		      			</h1>
		      		: ""
		      }
		      <div id="icon-holder">
		        {existingUser ? // important: go read the "paddingRight" comment a few lines above if you add or remove <span className="iconLink">
		        	<span style={{ position: "absolute", right: 0, display: "flex" }}>
						<span className="iconLink" style={activeKey === "SecureEFiling" ? {borderTop: "0.4rem solid #284162", marginTop: "-0.8rem", paddingTop: "0.4rem", textAlign: "center", order: 1} : {order: 1}}>
							<button title={messages[currentLang()]["sima.btn.efiling"]} className="glyphicon glyphicon-file brdr-0" style={{width:"3.8rem"}} onClick={activeKey === "SecureEFiling" ? () => {} : () => setAppearPopupEFiling(true)}></button>
							<span aria-label={messages[currentLang()]["sima.btn.efiling"]} title={messages[currentLang()]["sima.btn.efiling"]} className="glyphicon glyphicon-arrow-up" style={{"cursor":"pointer"}} onClick={activeKey === "SecureEFiling" ? () => {} : () => setAppearPopupEFiling(true)} tabIndex="-1" />
						</span>
						<span className="iconLink" style={activeKey === "Account" ? {borderTop: "0.4rem solid #284162", marginTop: "-0.8rem", paddingTop: "0.4rem", textAlign: "center", order: 2} : {order: 2}}>
							<button aria-label={messages[currentLang()]["account.title"]} title={messages[currentLang()]["account.title"]} className="glyphicon glyphicon-user brdr-0" onClick={activeKey === "Account" ? () => {} : () => setAppearPopupAccount(true)}></button>
						</span>
						<span className="iconLink" style={{order: 3}}>
							<button aria-label={messages[currentLang()]["wet.signOut"]} title={messages[currentLang()]["wet.signOut"]} className="glyphicon glyphicon-log-out brdr-0" onClick={() => setAppearPopupLogOut(true)}></button>
						</span>
		        	</span>
		        :
		        	<>
		        	  {activeKey === "Exhibits" ?
		        	  	<span className="iconLink">
		        	  	  <Link routeName="Log_in" params={currentLangParam()}>
		        	  	    <button
		        	  	      title={messages[currentLang()]["wet.signIn"]}
		        	  	      className="glyphicon glyphicon-log-in brdr-0"
		        	  	      onClick={() => setAppearPopupLogIn(true)}
		        	  	      tabIndex={0}
		        	  	    >
		        	  	    </button>
		        	  	  </Link>
		        	  	</span>
		        	  :
		        	  	<></>
		        	  }
		        	</>
		        }
		      </div>
		      <div style={{ clear : "both" }}></div>
		      <SceneView
		        component={descriptor.getComponent()}
		        navigation={descriptor.navigation}
		    />
		    </AppContext.Provider>
		    {/* inactivityTime="15000" reactionTime="10000" -> for test purposes */}
		    {
		    	pauseTimeout === false ?
		    		<FormidableSessionTimeout onSignOut={handleSignOut} />
		    	:
		    		<></>
		    }
		    <DateModified versionNumber={versionNumber} dateModified={dateModified} />
		  </main>
		  <Footer />
		</IntlProvider>
	)
}

//*important* react-navigation is primarily for native webapps written with react-native,
//we utilize the 'web-support' feature in regular react for a more consistent code base between website development and native app development
//for more info on how this works, see: https://reactnavigation.org/docs/3.x/web-support/
const AppNavigator = createNavigator(
	TemplateView
	, SwitchRouter(scenes)
	, {}
);

//helper functions
function fillInMissingBreadcrumbParams(breadcrumbs, params) {
	let updatedBreadcrumbs = [...breadcrumbs];

	updatedBreadcrumbs.forEach((breadcrumb) => {
		if (!!breadcrumbs && !!(breadcrumb.props) && !!(breadcrumb.props.params)) {
			for (const [key, value] of Object.entries(breadcrumb.props.params)) {
				if (value === "{"+key+"}") {
					breadcrumb.props.params[key] = params[key];
				}
			}
		}
	});
	return updatedBreadcrumbs;
}

const App = createBrowserApp(AppNavigator);
export default App;
export {TemplateView};